import { changeOverRange, type Change } from './changeOverRange';
import { readout, readoutRows, startValue } from './panelData';
import type { ChartSeries, Unit } from './seriesTypes';

// The model behind the phone values strip: what each outcome is worth now (or
// under the finger) and what it did since the range started. The floating
// tooltip reads the same values through readoutRows.

export interface StripRow {
    id: string;
    label: string;
    color: string;
    unit: Unit;
    /** Null when the outcome has no reading at the finger yet (it starts later). */
    value: number | null;
    change: Change | null;
}

// Only values that have a change in points or percent: not volume, flow or
// markers.
export const hasChange = (s: ChartSeries): boolean =>
    s.hasData &&
    s.data.kind !== 'markers' &&
    s.draw !== 'bars' &&
    (s.unit === 'probability' || s.unit === 'price');

/**
 * The rows of the strip: one per outcome that has data in the shown range,
 * with its value now (or under the finger) and the change since the range
 * start.
 *
 * The rows and their order come from the right edge of the loaded range
 * (`loaded`, the whole span the chart holds data for; the shown range when
 * omitted) and never from the finger or from the shown range's own edge: a
 * strip that gains, loses or reshuffles rows while a finger moves along the
 * chart, or while a pinch or pan moves the shown range's right edge, changes
 * height under that finger and the chart below it jumps. An outcome with no
 * reading at the finger or at the shown range's end (it starts later) keeps
 * its row and has no value in it.
 */
export function stripRows(
    series: readonly ChartSeries[],
    domain: readonly [number, number],
    cursor: number | null,
    loaded: readonly [number, number] = domain
): StripRow[] {
    return readoutRows(series.filter(hasChange), loaded[1]).map(
        ({ series: s }) => {
            const value = readout(s, cursor ?? domain[1]);
            return {
                id: s.id,
                label: s.label,
                color: s.color,
                unit: s.unit,
                value,
                change: changeOverRange(
                    s.unit,
                    startValue(s, domain[0]),
                    value
                ),
            };
        }
    );
}

/** The one unit every row shares, or null when they differ (or there is none). */
export function sharedUnit(rows: readonly StripRow[]): Unit | null {
    const first = rows[0]?.unit;
    return first !== undefined && rows.every(r => r.unit === first)
        ? first
        : null;
}

const CLOCK = {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
} as const;

/** "Oct 26, 00:00": the moment under the finger. */
export function formatStripTime(ms: number): string {
    return new Date(ms).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        ...CLOCK,
    });
}

const TWO_DAYS = 2 * 86_400_000;

/** When the change started: "12:00" within two days, "Oct 24" beyond. */
export function sinceText(startMs: number, spanMs: number): string {
    const start = new Date(startMs);
    return spanMs <= TWO_DAYS
        ? start.toLocaleTimeString(undefined, CLOCK)
        : start.toLocaleDateString(undefined, {
              month: 'short',
              day: 'numeric',
          });
}
